import React, { useRef, useEffect, useState } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ReferenceArea,
    TooltipProps,
} from 'recharts';
import { Box } from '@mui/material';
import HeightTimeLegend from './HeightTimeLegend';

interface Point {
    time: number;
    y: number;
}

interface EventMarker {
    time: number;
    event: string;
}
interface CustomTooltipProps extends TooltipProps<number, string> {
    eventMarkers: EventMarker[];
}

interface Props {
    series: Point[];
    eventMarkers?: EventMarker[];
    drillMetrics?: {
        potential_saved_time?: number;
        motion_time?: number;
        reaction_time?: number;
    };
}

const EVENT_MAP: Record<
    string,
    { color: string; dash: string; label: string }
> = {
    buzzer_start: {
        color: '#fff',
        dash: '5 2',
        label: 'Buzzer Start',
    },
    first_shot_start: {
        color: '#F05E7C',
        dash: '5 2',
        label: 'First Shot',
    },
    presentation_start_time: {
        color: '#80D4DF',
        dash: '5 2',
        label: 'Presentation Start',
    },
    presentation_end_time: {
        color: '#988AE4',
        dash: '5 2',
        label: 'Presentation End',
    },
    //   time_slack: {
    //     color: '#FFD700',
    //     dash: '4 2',
    //     label: 'Time Slack',
    //   },
};

// ...imports stay the same

export default function HeightTimeChart({
    series,
    eventMarkers = [],
    drillMetrics,
}: Props) {
    const chartRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(800);

    useEffect(() => {
        const update = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    if (!series.length) return null;

    const buzzerStartRaw =
        eventMarkers.find((e) => e.event === 'buzzer_start')?.time ?? series[0].time;

    const formattedSeries = series.map((p) => ({
        ...p,
        time: p.time - buzzerStartRaw,
    }));

    const formattedEvents = eventMarkers.map((e) => ({
        ...e,
        time: e.time - buzzerStartRaw,
    }));

    const firstShotStart = formattedEvents.find(
        (e) => e.event === 'first_shot_start'
    )?.time;
    const presentationStart = formattedEvents.find(
        (e) => e.event === 'presentation_start_time'
    )?.time;
    const presentationEnd = formattedEvents.find(
        (e) => e.event === 'presentation_end_time'
    )?.time;

    const allEvents = [...formattedEvents];
    if (firstShotStart !== undefined && drillMetrics?.potential_saved_time) {
        allEvents.push({
            event: 'time_slack',
            time: firstShotStart - drillMetrics.potential_saved_time,
        });
    }

    // Focused domain
    const domainStart = -0.1;

    const domainEnd = firstShotStart !== undefined ? firstShotStart + 0.1 : formattedSeries.at(-1)?.time ?? 1;

    // Filter data within domain
    const croppedSeries = formattedSeries.filter(
        (p) => p.time >= domainStart && p.time <= domainEnd
    );
    const CustomTooltip: React.FC<CustomTooltipProps> = ({
        active,
        payload,
        label,
        eventMarkers,
    }) => {
        if (!active || !payload || !payload.length) return null;

        const hoveredTime = label as number;

        const eventAtLabel = eventMarkers.find(
            (e) => Math.abs(e.time - hoveredTime) < 0.01
        );

        const eventLabel =
            eventAtLabel && EVENT_MAP[eventAtLabel.event]
                ? EVENT_MAP[eventAtLabel.event].label
                : eventAtLabel?.event.replace(/_/g, ' ');

        return (
            <Box
                sx={{
                    backgroundColor: '#1e1e1e',
                    border: '1px solid #333',
                    borderRadius: 1,
                    padding: 1,
                    color: '#fff',
                    fontSize: 13,
                }}
            >
                <div>
                    <strong>Time:</strong> {hoveredTime.toFixed(3)}s
                </div>
                {eventAtLabel && (
                    <div>
                        {eventLabel}
                    </div>
                )}
            </Box>
        );
    };

    function renderCustomMetrics(width: number, height: number, xScale: (x: number) => number) {
        const yBase = height - 80;
        const labels = [
            drillMetrics?.reaction_time !== undefined &&
            presentationStart !== undefined && {
                label: 'Reaction Time',
                value: drillMetrics.reaction_time,
                from: formattedEvents.find((e) => e.event === 'buzzer_start')!.time,
                to: presentationStart,
            },
            drillMetrics?.motion_time !== undefined &&
            presentationStart !== undefined &&
            presentationEnd !== undefined && {
                label: 'Motion Time',
                value: drillMetrics.motion_time,
                from: presentationStart,
                to: presentationEnd,
            },
        ].filter(Boolean) as { label: string; value: number; from: number; to: number }[];

        if (!labels.length) return null;

        return (
            <svg width={width} height={height + 60} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
                {labels.map(({ label, value, from, to }) => {
                    const x = xScale((from + to) / 2);
                    return (
                        <g key={label}>
                            <text x={x} y={yBase - 5} textAnchor="middle" fontSize={12} fill="#FFFFFF" fontWeight={500} style={{ textShadow: '0 0 4px #000' }}>
                                {label}
                            </text>
                            <text x={x} y={yBase + 10} textAnchor="middle" fontSize={12} fill="#FFFFFF" fontWeight={400} style={{ textShadow: '0 0 4px #000' }}>
                                {value.toFixed(2)}s
                            </text>
                        </g>
                    );
                })}
            </svg>
        );
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', width: '100%', height: 320 }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
                <LineChart
                    data={croppedSeries}
                    width={containerWidth}
                    height={320}
                    margin={{ top: 10, right: 50, bottom: 60, left: 20 }}
                    ref={chartRef}
                >
                    <CartesianGrid stroke="#ccc" strokeDasharray="2 2" />
                    <XAxis
                        type="number"
                        dataKey="time"
                        domain={[domainStart, domainEnd]}
                        orientation="bottom"
                        stroke="#fff"
                        axisLine={{ stroke: '#fff', strokeWidth: 2 }}
                        tickLine={{ stroke: '#fff' }}
                        tickFormatter={(value) => value.toFixed(1)}
                        label={{ value: 'Time (s)', position: 'top', dy: 55 }}
                        tickCount={10}
                    />
                    <YAxis
                        stroke="#fff"
                        axisLine={{ stroke: '#fff', strokeWidth: 2 }}
                        tickLine={{ stroke: '#fff' }}
                        label={{ value: 'Height (m)', angle: -90, position: 'insideLeft', dx: -10 }}
                    />
                    <Tooltip content={<CustomTooltip eventMarkers={allEvents} />} />

                    <Line type="monotone" dataKey="y" stroke="#fff" strokeWidth={2} dot={false} />

                    {/* Reference Areas */}
                    {presentationStart !== undefined && (
                        <ReferenceArea x1={0} x2={presentationStart} fill="#52868F" fillOpacity={0.6} />
                    )}
                    {presentationStart !== undefined && presentationEnd !== undefined && (
                        <ReferenceArea x1={presentationStart} x2={presentationEnd} fill="#66628E" fillOpacity={0.5} />
                    )}
                    {presentationEnd !== undefined && firstShotStart !== undefined && (
                        <ReferenceArea x1={presentationEnd} x2={firstShotStart} fill="#9D5E77" fillOpacity={0.5} />
                    )}

                    {allEvents.map((e, idx) => {
                        const { color, dash } = EVENT_MAP[e.event] || { color: '#666', dash: '1 1' };
                        return (
                            <ReferenceLine
                                key={`${e.event}-${idx}`}
                                x={e.time}
                                stroke={color}
                                strokeDasharray={dash}
                                strokeWidth={3}
                            />
                        );
                    })}
                </LineChart>

                {/* Labels */}
                {chartRef.current?.state?.xAxisMap && renderCustomMetrics(containerWidth, 290, chartRef.current.state.xAxisMap[0].scale)}
            </Box>
        </Box>
    );
}
